import { useState, useRef, useEffect } from "react";
import type { SseFoodMessage, FoodItem } from "@/api/aiChat";
import { INITIAL_SHOW_COUNT, LAZY_LOAD_STEP } from "./train-card";
import { stripHtml } from "@/utils/format";

// ════════════════════════════════════════════════════
//  美食/餐厅列表卡片（与 TrainCard/CarCard 同风格 + 懒加载）
// ════════════════════════════════════════════════════

/** 餐厅类型图标颜色 */
const FOOD_TYPE_ICON: Record<string, { bg: string; color: string; icon: string }> = {
  "火锅店": { bg: "linear-gradient(135deg, #EF4444, #DC2626)", color: "#fff", icon: "ri-fire-line" },
  "中餐厅": { bg: "linear-gradient(135deg, #F97316, #EA580C)", color: "#fff", icon: "ri-restaurant-line" },
};

/** 解析标签字符串为数组（限制显示数量） */
function parseTags(tagStr: string, max: number = 6): string[] {
  if (!tagStr) return [];
  return tagStr.split(",").filter(Boolean).slice(0, max);
}

interface FoodQueryRaw {
  city: string;
  total: number;
  foods: FoodItem[];
}

export default function FoodCard({ data, onFoodSelect }: { data: SseFoodMessage | FoodQueryRaw; onFoodSelect?: (food: FoodItem) => void }) {
  const fd = ("data" in data && (data as SseFoodMessage).data)
    ? (data as SseFoodMessage).data
    : data as FoodQueryRaw;

  if (!fd?.foods?.length) return null;

  // ★ 懒加载状态（复用常量）
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW_COUNT);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const foods = fd.foods;
  const showFoods = foods.slice(0, visibleCount);
  const isAllLoaded = visibleCount >= foods.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isAllLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((prev) => Math.min(prev + LAZY_LOAD_STEP, foods.length));
      },
      { rootMargin: "80px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAllLoaded, foods.length]);

  /** 根据type字段提取餐厅子类型 */
  function getSubType(typeStr: string): string {
    const parts = typeStr.split(";");
    return parts[parts.length - 1] || parts[0] || "餐厅";
  }

  /** 获取类型样式 */
  function getTypeStyle(typeStr: string) {
    const subType = getSubType(typeStr);
    return FOOD_TYPE_ICON[subType] || FOOD_TYPE_ICON["中餐厅"];
  }

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5 flex-wrap"
        style={{ background: "linear-gradient(135deg, #FEF2F2, #FEE2E2)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-restaurant-2-line" style={{ color: "#EF4444", fontSize: 15 }} />
        <span className="text-[12px] font-semibold" style={{ color: "#333" }}>
          {fd.city}美食推荐
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono"
          style={{ background: "#FECACA", color: "#DC2626" }}
        >
          共 {fd.total} 家
        </span>
        {!isAllLoaded && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#FECACA", color: "#DC2626" }}>
            已显示 {visibleCount}/{foods.length}
          </span>
        )}
      </div>

      {/* 餐厅列表 */}
      <div className="flex flex-col">
        {showFoods.map((food: FoodItem, idx: number) => {
          const ts = getTypeStyle(food.type);
          const tags = parseTags(food.tag);

          return (
            <div
              key={food.id + idx}
              className={`px-3 py-2.5 ${onFoodSelect ? "cursor-pointer active:scale-[0.995]" : "active:bg-[#FEF2F2]"}`}
              style={{
                transition: "background 0.15s, transform 0.1s",
                borderBottom: idx < showFoods.length - 1 ? "1px solid #F0F2F8" : "none",
              }}
              onClick={() => onFoodSelect?.(food)}
            >
              {/* 第一行：名称 + 类型标签 + 评分 */}
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <i className={ts.icon + " flex-shrink-0"} style={{ color: "#EF4444", fontSize: 13 }} />
                  <span className="text-[12px] font-bold truncate" style={{ color: "#1E293B", maxWidth: "120px" }}>
                    {food.name}
                  </span>
                  <span
                    className="text-[8px] font-medium px-1 py-0.5 rounded flex-shrink-0"
                    style={{ background: ts.bg, color: ts.color }}
                  >
                    {getSubType(food.type)}
                  </span>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  {/* 评分 */}
                  <span className="text-[10px] font-semibold px-1 py-0.5 rounded"
                    style={{ background: "#FEF3C7", color: "#D97706" }}
                  >
                    <i className="ri-star-fill" style={{ fontSize: 9 }} /> {food.rating}
                  </span>
                  {/* 距离 */}
                  {food.distance > 0 && (
                    <span className="text-[10px]" style={{ color: "#9CA3AF" }}>
                      {food.distance >= 1000 ? (food.distance / 1000).toFixed(1) + "km" : food.distance + "m"}
                    </span>
                  )}
                </div>
              </div>

              {/* 第二行：地址 */}
              <div className="flex items-center gap-1 mb-1 text-[10px]" style={{ color: "#94A3B8" }}>
                <i className="ri-map-pin-2-line flex-shrink-0" style={{ fontSize: 11, color: "#CBD5E1" }} />
                <span className="truncate">{stripHtml(food.district)} · {stripHtml(food.address)}</span>
              </div>

              {/* ★ 电话 */}
              {food.tel && (
                <div className="flex items-center gap-1 mb-1 text-[10px]" style={{ color: "#94A3B8" }}>
                  <i className="ri-phone-line flex-shrink-0" style={{ fontSize: 10, color: "#CBD5E1" }} />
                  <span className="truncate">{stripHtml(food.tel)}</span>
                </div>
              )}

              {/* ★ 餐厅图片 */}
              {food.photos?.length > 0 && (
                <div className="flex items-center gap-1.5 mb-1.5 overflow-x-auto">
                  {food.photos.slice(0, 3).map((p, pIdx) => (
                    <img
                      key={pIdx}
                      src={p}
                      alt={food.name}
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      style={{ border: "1px solid #FEE2E2" }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  ))}
                </div>
              )}

              {/* 第三行：标签（可换行） */}
              {tags.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap text-[9px]">
                  {tags.map((t, i) => (
                    <span key={i} className="px-1 py-0.5 rounded whitespace-nowrap"
                      style={{ background: "#FFF7ED", color: "#C2410A", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {t.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* 懒加载哨兵 */}
        {!isAllLoaded && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "#AAA" }}>
              <i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />
              <span>向下滑动加载更多...</span>
            </div>
          </div>
        )}

        {/* 全部加载完毕 */}
        {isAllLoaded && foods.length > INITIAL_SHOW_COUNT && (
          <div className="flex items-center justify-center py-2" style={{ borderTop: "1px solid #F0F2F8" }}>
            <span className="text-[10px]" style={{ color: "#C0C8D4" }}>
              — 已展示全部 {foods.length} 家餐厅 —
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
